import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Virtuoso } from 'react-virtuoso';
import PropTypes from 'prop-types';

// Custom hook for React 19 compatibility - replaces AutoSizer
const useElementSize = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current) return;

    const updateSize = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setSize({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    // Initial size check
    updateSize();

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(ref.current);

    // Fallback: check size again after a brief delay in case flex layout hasn't settled
    const timeout = setTimeout(updateSize, 100);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, []);

  return [ref, size];
};

export const VirtualList = ({
  data,
  hasMoreRows,
  onLoadMoreItems,
  overscan = 100,
  renderRow,
  loadingMoreRows,
  onHeightChanged,
  Components,
}) => {
  const [containerRef, { height, width }] = useElementSize();

  // Ensure data is always an array
  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const itemContent = useCallback(
    (index) => {
      const item = safeData[index];
      return renderRow(index, item);
    },
    [safeData, renderRow],
  );

  const handleEndReached = useCallback(
    (i) => {
      if (hasMoreRows && !loadingMoreRows) {
        onLoadMoreItems(i);
      }
    },
    [hasMoreRows, loadingMoreRows, onLoadMoreItems],
  );

  const virtuosoProps = useMemo(() => {
    const props = {
      style: { height, width },
      totalCount: safeData.length,
      endReached: handleEndReached,
      overscan,
      itemContent,
      totalListHeightChanged: onHeightChanged,
    };

    // Only add components if Footer exists
    if (Components?.Footer) {
      props.components = {
        Footer: () => Components.Footer,
      };
    }

    return props;
  }, [
    height,
    width,
    safeData.length,
    handleEndReached,
    overscan,
    itemContent,
    onHeightChanged,
    Components,
  ]);

  return (
    <div ref={containerRef} className='flex' style={{ minHeight: 0 }}>
      {height > 0 && width > 0 && safeData.length > 0 && (
        <Virtuoso {...virtuosoProps} />
      )}
    </div>
  );
};

VirtualList.defaultProps = {
  hasMoreRows: false,
  onLoadMoreItems: () => null,
  overscan: 100, // pixels i think
  renderRow: () => null,
  components: {},
  onHeightChanged: () => null,
};

VirtualList.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasMoreRows: PropTypes.bool,
  overscan: PropTypes.number,
  onLoadMoreItems: PropTypes.func,
  onHeightChanged: PropTypes.func,
  renderRow: PropTypes.func.isRequired,
  loadingMoreRows: PropTypes.bool,
  Components: PropTypes.shape({
    Footer: PropTypes.any,
  }),
};
